import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Popover, PopoverAnchor, PopoverContent } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { getRooms, getProperties, createRoom, updateRoom, deleteRoom, getGuestLedgerDues, getBookings } from '../lib/api';
import { toast } from 'sonner';
import { Home, Plus, Pencil, Trash2, BedDouble, User, Search, Check, Loader2 } from 'lucide-react';
import { IconTip } from '../components/IconTip';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Smart Rent Display Component
const RentDisplay = ({ room }) => {
  const beds = room.beds || [];
  const defaultRent = room.rent_per_bed || 0;
  
  // Get actual rents: use guest_rent for occupied beds, bed rent for vacant, or default
  const rents = beds.map(b => {
    // For occupied beds, use guest's actual rent if available
    if (b.guest_rent) return b.guest_rent;
    // Otherwise use bed's set rent or default
    return b.rent || defaultRent;
  }).filter(r => r > 0);
  
  if (rents.length === 0) {
    return <span>₹{defaultRent.toLocaleString()}/bed</span>;
  }
  
  const uniqueRents = [...new Set(rents)];
  const minRent = Math.min(...rents);
  const maxRent = Math.max(...rents);
  
  // All beds have same rent
  if (uniqueRents.length === 1) {
    return <span>₹{uniqueRents[0].toLocaleString()}/bed</span>;
  }
  
  // Different rents - show range
  return (
    <span className="text-amber-700">
      ₹{minRent.toLocaleString()} - ₹{maxRent.toLocaleString()}
    </span>
  );
};

const BedIcon = ({ status }) => {
  // Vacant=RED, Occupied=GREEN, Booked=BLUE, Notice=AMBER
  const statusColors = {
    vacant: 'text-rose-600',
    occupied: 'text-emerald-500',
    booked: 'text-blue-600',
    notice: 'text-amber-600'
  };

  return <BedDouble className={`h-5 w-5 ${statusColors[status] || statusColors.vacant}`} />;
};

const StatusBadge = ({ status }) => {
  // Vacant=RED, Occupied=GREEN, Booked=BLUE, Notice=AMBER
  const variants = {
    vacant: 'bg-rose-100 text-rose-700',
    occupied: 'bg-emerald-100 text-emerald-700',
    booked: 'bg-blue-100 text-blue-700',
    notice: 'bg-amber-100 text-amber-700'
  };

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${variants[status] || variants.vacant}`}>
      {status}
    </span>
  );
};

export const Rooms = () => {
  const { canAdd, canEdit, canDelete } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [properties, setProperties] = useState([]);
  const [bookedBeds, setBookedBeds] = useState({});
  const [selectedProperty, setSelectedProperty] = useState(searchParams.get('property_id') || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyVacant, setOnlyVacant] = useState(false);
  const [selectedSharing, setSelectedSharing] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [form, setForm] = useState({
    property_id: '',
    floor_no: 1,
    flat_no: '',
    room_no: '',
    sharing_type: 2,
    rent_per_bed: 0,
    deposit_per_bed: 0
  });
  const [editForm, setEditForm] = useState({
    sharing_type: 2,
    rent_per_bed: 0,
    deposit_per_bed: 0
  });
  // Bed tile interaction: single-click shows dues popover, double-click navigates
  const [openBedKey, setOpenBedKey] = useState(null);
  const [bedDues, setBedDues] = useState(null);
  const [bedDuesLoading, setBedDuesLoading] = useState(false);
  const clickTimer = useRef(null);

  const fetchBedDues = async (guestId) => {
    setBedDues(null);
    setBedDuesLoading(true);
    try {
      const res = await getGuestLedgerDues(guestId);
      setBedDues(res.data);
    } catch (err) {
      setBedDues(null);
    } finally {
      setBedDuesLoading(false);
    }
  };

  const handleBedSingleClick = (room, bed) => {
    // Only for non-vacant beds with a guest — show dues popover. Vacant: do nothing.
    if (bed.status === 'vacant' || !bed.guest_id) return;
    const key = `${room.id}-${bed.bed_no}`;
    if (openBedKey === key) {
      setOpenBedKey(null);
      return;
    }
    setOpenBedKey(key);
    fetchBedDues(bed.guest_id);
  };

  const handleBedDoubleClick = (room, bed) => {
    setOpenBedKey(null);
    if (bed.status === 'vacant') {
      if (canAdd('guests')) {
        navigate(`/guests?action=add&property_id=${room.property_id}&room_id=${room.id}&bed_no=${bed.bed_no}`);
      }
    } else if (bed.guest_id) {
      navigate(`/guests?guest_id=${bed.guest_id}`);
    }
  };

  const onBedClick = (room, bed) => {
    if (clickTimer.current) return; // a double-click is in progress
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      handleBedSingleClick(room, bed);
    }, 220);
  };

  const onBedDoubleClick = (room, bed) => {
    if (clickTimer.current) { clearTimeout(clickTimer.current); clickTimer.current = null; }
    handleBedDoubleClick(room, bed);
  };

  useEffect(() => {
    fetchData();
  }, [selectedProperty]);

  const fetchData = async () => {
    try {
      const [roomsRes, propsRes, bookingsRes] = await Promise.all([
        getRooms(selectedProperty === 'all' ? null : selectedProperty),
        getProperties(),
        getBookings()
      ]);
      // Sort rooms by combined_number (alphanumeric sorting)
      const sortedRooms = [...roomsRes.data].sort((a, b) => {
        const aNum = a.combined_number || '';
        const bNum = b.combined_number || '';
        // Natural sort - handles numbers within strings correctly
        return aNum.localeCompare(bNum, undefined, { numeric: true, sensitivity: 'base' });
      });
      setRooms(sortedRooms);
      setProperties(propsRes.data);
      // Map active-booking preferred beds so Rooms can flag them (only shown when the bed is actually vacant)
      const bm = {};
      (bookingsRes.data || []).forEach(b => {
        if (['pending', 'confirmed'].includes(b.status) && b.preferred_room_id && b.preferred_bed_no) {
          bm[`${b.preferred_room_id}-${b.preferred_bed_no}`] = b.name;
        }
      });
      setBookedBeds(bm);
    } catch (err) {
      toast.error('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createRoom(form);
      toast.success('Room created');
      setDialogOpen(false);
      setForm({
        property_id: '',
        floor_no: 1,
        flat_no: '',
        room_no: '',
        sharing_type: 2,
        rent_per_bed: 0,
        deposit_per_bed: 0
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create room');
    }
  };

  const handleEdit = (room) => {
    setEditingRoom(room);
    setEditForm({
      sharing_type: room.sharing_type,
      rent_per_bed: room.rent_per_bed,
      deposit_per_bed: room.deposit_per_bed
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      // Only send bed_rents for vacant beds (don't override occupied/notice beds)
      const filteredForm = { ...editForm };
      if (filteredForm.bed_rents && editingRoom?.beds) {
        filteredForm.bed_rents = filteredForm.bed_rents.filter((br) => {
          // Find the bed by bed_no to get its current status
          const bed = editingRoom.beds.find(b => b.bed_no === br.bed_no);
          // Only include if bed is vacant (allow editing vacant beds only)
          return bed && bed.status === 'vacant';
        });
        // If no vacant beds have changes, remove bed_rents entirely
        if (filteredForm.bed_rents.length === 0) {
          delete filteredForm.bed_rents;
        }
      }
      await updateRoom(editingRoom.id, filteredForm);
      toast.success('Room updated');
      setEditDialogOpen(false);
      setEditingRoom(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update room');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this room?')) return;
    try {
      await deleteRoom(id);
      toast.success('Room deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800" />
      </div>
    );
  }

  const roomHasVacancy = (room) => (room.beds || []).some(b => b.status === 'vacant');
  const vacantBedCount = rooms.reduce((sum, r) => sum + (r.beds || []).filter(b => b.status === 'vacant').length, 0);
  const roomsWithVacancy = rooms.filter(roomHasVacancy).length;
  const sharingTypes = [...new Set(rooms.map(r => r.sharing_type).filter(Boolean))].sort((a, b) => a - b);
  const displayedRooms = rooms.filter(r => {
    const matchesSearch = !searchQuery || (r.combined_number || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesVacant = !onlyVacant || roomHasVacancy(r);
    const matchesSharing = selectedSharing === 'all' || r.sharing_type === parseInt(selectedSharing);
    return matchesSearch && matchesVacant && matchesSharing;
  });

  return (
    <div className="space-y-6" data-testid="rooms-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Rooms</h1>
          <p className="text-slate-500 mt-1">Manage rooms and beds (Dollhouse View)</p>
        </div>
        <div className="flex gap-3 flex-wrap items-center">
          {/* Search by room number */}
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search room no..."
              className="pl-9 w-[180px]"
              data-testid="room-search-input"
            />
          </div>
          <Select value={selectedProperty} onValueChange={setSelectedProperty}>
            <SelectTrigger className="w-[160px]" data-testid="property-filter">
              <SelectValue placeholder="All Properties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Properties</SelectItem>
              {properties.map((prop) => (
                <SelectItem key={prop.id} value={prop.id}>{prop.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            {canAdd('rooms') && (
              <DialogTrigger asChild>
                <Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-room-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Room
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Room</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Property</Label>
                  <Select
                    value={form.property_id}
                    onValueChange={(val) => setForm({ ...form, property_id: val })}
                  >
                    <SelectTrigger data-testid="room-property-select">
                      <SelectValue placeholder="Select property" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map((prop) => (
                        <SelectItem key={prop.id} value={prop.id}>{prop.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Floor No</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.floor_no}
                      onChange={(e) => setForm({ ...form, floor_no: parseInt(e.target.value) })}
                      required
                      data-testid="room-floor-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Flat No</Label>
                    <Input
                      value={form.flat_no}
                      onChange={(e) => setForm({ ...form, flat_no: e.target.value.toUpperCase() })}
                      placeholder="e.g., A"
                      required
                      data-testid="room-flat-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Room No</Label>
                    <Input
                      value={form.room_no}
                      onChange={(e) => setForm({ ...form, room_no: e.target.value.toUpperCase() })}
                      placeholder="e.g., 1"
                      required
                      data-testid="room-no-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Sharing Type (1-7 beds)</Label>
                  <Select
                    value={form.sharing_type.toString()}
                    onValueChange={(val) => setForm({ ...form, sharing_type: parseInt(val) })}
                  >
                    <SelectTrigger data-testid="room-sharing-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                        <SelectItem key={num} value={num.toString()}>
                          {num} Sharing
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Rent per Bed (₹)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.rent_per_bed}
                      onChange={(e) => setForm({ ...form, rent_per_bed: parseFloat(e.target.value) })}
                      required
                      data-testid="room-rent-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Deposit per Bed (₹)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.deposit_per_bed}
                      onChange={(e) => setForm({ ...form, deposit_per_bed: parseFloat(e.target.value) })}
                      required
                      data-testid="room-deposit-input"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-blue-800 hover:bg-blue-900" data-testid="save-room-btn">
                    Create Room
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Room Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Room - {editingRoom?.combined_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Sharing Type (1-7 beds)</Label>
              <Select
                value={editForm.sharing_type.toString()}
                onValueChange={(val) => setEditForm({ ...editForm, sharing_type: parseInt(val) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                    <SelectItem key={num} value={num.toString()}>
                      {num} Sharing
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Default Rent per Bed (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  value={editForm.rent_per_bed}
                  onChange={(e) => setEditForm({ ...editForm, rent_per_bed: parseFloat(e.target.value) })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Default Deposit per Bed (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  value={editForm.deposit_per_bed}
                  onChange={(e) => setEditForm({ ...editForm, deposit_per_bed: parseFloat(e.target.value) })}
                  required
                />
              </div>
            </div>
            
            {/* Individual Bed Rents & Deposits */}
            {editingRoom?.beds && editingRoom.beds.length > 0 && (
              <div className="space-y-3 border-t pt-4">
                <Label className="text-amber-700">Individual Bed Rent & Deposit (Optional)</Label>
                <p className="text-xs text-slate-500 mb-2">Edit rent/deposit for vacant beds only. Occupied beds show guest's current rent (read-only).</p>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {editingRoom.beds.map((bed, idx) => {
                    const isOccupied = bed.status !== 'vacant';
                    const guestRent = bed.guest_rent || bed.rent || editForm.rent_per_bed;
                    const guestDeposit = bed.guest_deposit || bed.deposit || editForm.deposit_per_bed;
                    
                    return (
                    <div key={bed.bed_no} className={`flex items-center gap-2 p-2 rounded ${isOccupied ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50'}`}>
                      <span className="text-xs font-mono w-8 font-bold">B{bed.bed_no}</span>
                      <div className="flex-1">
                        {isOccupied ? (
                          <div className="h-8 px-3 py-1 bg-emerald-100 rounded text-sm text-emerald-700 flex items-center">
                            ₹{guestRent.toLocaleString()} <span className="text-xs ml-1 text-emerald-600">(Guest)</span>
                          </div>
                        ) : (
                        <Input
                          type="number"
                          min="0"
                          placeholder={`Rent: ${editForm.rent_per_bed || 0}`}
                          value={editForm.bed_rents?.[idx]?.rent || ''}
                          onChange={(e) => {
                            const newBedRents = [...(editForm.bed_rents || editingRoom.beds.map(b => ({ bed_no: b.bed_no, rent: b.rent || editForm.rent_per_bed, deposit: b.deposit || editForm.deposit_per_bed })))];
                            newBedRents[idx] = { 
                              ...newBedRents[idx], 
                              bed_no: bed.bed_no,
                              rent: parseFloat(e.target.value) || editForm.rent_per_bed 
                            };
                            setEditForm({ ...editForm, bed_rents: newBedRents });
                          }}
                          className="h-8 text-sm"
                        />
                        )}
                      </div>
                      <div className="flex-1">
                        {isOccupied ? (
                          <div className="h-8 px-3 py-1 bg-emerald-100 rounded text-sm text-emerald-700 flex items-center">
                            ₹{guestDeposit.toLocaleString()}
                          </div>
                        ) : (
                        <Input
                          type="number"
                          min="0"
                          placeholder={`Deposit: ${editForm.deposit_per_bed || 0}`}
                          value={editForm.bed_rents?.[idx]?.deposit || ''}
                          onChange={(e) => {
                            const newBedRents = [...(editForm.bed_rents || editingRoom.beds.map(b => ({ bed_no: b.bed_no, rent: b.rent || editForm.rent_per_bed, deposit: b.deposit || editForm.deposit_per_bed })))];
                            newBedRents[idx] = { 
                              ...newBedRents[idx], 
                              bed_no: bed.bed_no,
                              deposit: parseFloat(e.target.value) || editForm.deposit_per_bed 
                            };
                            setEditForm({ ...editForm, bed_rents: newBedRents });
                          }}
                          className="h-8 text-sm"
                        />
                        )}
                      </div>
                      <span className={`text-xs ${bed.status === 'vacant' ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {bed.status === 'vacant' ? 'Vacant' : bed.status === 'notice' ? 'Notice' : 'Occupied'}
                      </span>
                    </div>
                  )})}
                </div>
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-blue-800 hover:bg-blue-900">
                Update Room
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vacancy summary, quick filter & color legend */}
      {rooms.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-slate-200 shadow-sm text-sm" data-testid="vacancy-summary">
              <span className="w-2 h-2 rounded-full bg-rose-500"></span>
              <span className="font-bold font-mono text-slate-900">{vacantBedCount}</span>
              <span className="text-slate-500">vacant bed{vacantBedCount !== 1 ? 's' : ''} across</span>
              <span className="font-bold font-mono text-slate-900">{roomsWithVacancy}</span>
              <span className="text-slate-500">room{roomsWithVacancy !== 1 ? 's' : ''}</span>
            </div>
            <button
              onClick={() => setOnlyVacant(v => !v)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm transition-colors ${onlyVacant ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              data-testid="has-vacancy-toggle"
            >
              <Check className="h-4 w-4" />
              Has vacancy
            </button>
            {sharingTypes.length > 1 && (
              <Select value={selectedSharing} onValueChange={setSelectedSharing}>
                <SelectTrigger className="w-[150px] h-[38px] bg-white" data-testid="sharing-type-filter">
                  <SelectValue placeholder="All Sharing" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sharing</SelectItem>
                  {sharingTypes.map(s => <SelectItem key={s} value={s.toString()}>{s} Sharing</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-600 flex-wrap">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-rose-100 border border-rose-300"></span>Vacant</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></span>Occupied</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-100 border border-dashed border-blue-500"></span>Booked</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300"></span>On Notice</span>
          </div>
        </div>
      )}

      {/* Rooms Grid (Dollhouse View) */}
      {rooms.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <Home className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Rooms Yet</h3>
            <p className="text-slate-500 mb-4">Add rooms to your properties</p>
          </CardContent>
        </Card>
      ) : displayedRooms.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <Search className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No rooms match your filters</h3>
            <p className="text-slate-500 mb-4">Try a different room number or turn off "Has vacancy".</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {displayedRooms.map((room, ridx) => {
            const totalBeds = (room.beds || []).length;
            const filledBeds = (room.beds || []).filter(b => b.status !== 'vacant').length;
            const occPct = totalBeds > 0 ? Math.round((filledBeds / totalBeds) * 100) : 0;
            return (
            <Card key={room.id} className="dash-animate card-hover relative overflow-hidden" style={{ animationDelay: `${ridx * 50}ms` }} data-testid={`room-card-${room.id}`}>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-indigo-600" />
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base font-mono">{room.combined_number}</CardTitle>
                    <p className="text-xs text-slate-500 mt-0.5">{room.property_name}</p>
                  </div>
                  {(canEdit('rooms') || canDelete('rooms')) && (
                    <div className="flex gap-1">
                      {canEdit('rooms') && (
                        <IconTip label="Edit Room"><Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(room)}
                          className="h-7 w-7"
                          data-testid={`edit-room-${room.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button></IconTip>
                      )}
                      {canDelete('rooms') && (
                        <IconTip label="Delete Room"><Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(room.id)}
                          className="h-7 w-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          data-testid={`delete-room-${room.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button></IconTip>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  <Badge variant="secondary" className="text-xs">
                    {room.sharing_type} Sharing
                  </Badge>
                  <Badge variant="outline" className="text-xs font-mono">
                    <RentDisplay room={room} />
                  </Badge>
                </div>
                {/* Per-room occupancy indicator */}
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-wide text-slate-400">Occupancy</span>
                    <span className={`text-[11px] font-bold font-mono ${filledBeds === totalBeds ? 'text-emerald-700' : filledBeds === 0 ? 'text-rose-600' : 'text-slate-700'}`}>
                      {filledBeds}/{totalBeds} filled
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${occPct === 100 ? 'bg-gradient-to-r from-emerald-500 to-teal-600' : occPct === 0 ? 'bg-gradient-to-r from-rose-500 to-pink-600' : 'bg-gradient-to-r from-amber-500 to-orange-500'}`}
                      style={{ width: `${Math.max(occPct, occPct === 0 ? 4 : 0)}%` }}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(room.sharing_type, 4)}, 1fr)` }}>
                  {room.beds?.map((bed) => {
                    // Use guest's actual rent for occupied beds, otherwise bed rent or default
                    const actualRent = bed.guest_rent || bed.rent || room.rent_per_bed;
                    
                    // Check if rents vary across beds (using actual rents)
                    const allRents = room.beds?.map(b => b.guest_rent || b.rent || room.rent_per_bed) || [];
                    const hasVariedRents = new Set(allRents).size > 1;

                    const isVacant = bed.status === 'vacant';
                    const bookedByName = bookedBeds[`${room.id}-${bed.bed_no}`];
                    const isBooked = isVacant && !!bookedByName;
                    const canAddGuest = isVacant && canAdd('guests');
                    const isClickable = canAddGuest || (!isVacant && bed.guest_id);
                    const bedKey = `${room.id}-${bed.bed_no}`;
                    const bedTitle = isBooked
                      ? `Booked for ${bookedByName}${canAddGuest ? ' · double-click to add guest' : ''}`
                      : isVacant
                      ? (canAddGuest ? 'Vacant · double-click to add guest' : 'Vacant')
                      : `${bed.guest_name || 'Occupied'} · click for dues, double-click to open`;

                    const tile = (
                      <div
                        onClick={isClickable ? () => onBedClick(room, bed) : undefined}
                        onDoubleClick={isClickable ? () => onBedDoubleClick(room, bed) : undefined}
                        role={isClickable ? 'button' : undefined}
                        tabIndex={isClickable ? 0 : undefined}
                        onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleBedDoubleClick(room, bed); } } : undefined}
                        className={`flex flex-col items-center p-2 rounded-md text-xs transition-all border-2 ${
                          isBooked ? 'bg-blue-50 border-dashed border-blue-500' :
                          bed.status === 'vacant' ? 'bg-rose-50 border-dashed border-rose-400' :
                          bed.status === 'occupied' ? 'bg-emerald-50 border-transparent' :
                          bed.status === 'booked' ? 'bg-blue-50 border-transparent' :
                          'bg-amber-50 border-transparent'
                        } ${isClickable ? 'cursor-pointer hover:ring-2 hover:ring-blue-400 hover:shadow-sm' : ''} ${openBedKey === bedKey ? 'ring-2 ring-blue-500' : ''}`}
                        title={bedTitle}
                        data-testid={isVacant ? `vacant-bed-${room.id}-${bed.bed_no}` : `occupied-bed-${room.id}-${bed.bed_no}`}
                      >
                        <BedIcon status={isBooked ? 'booked' : bed.status} />
                        <span className="mt-1 font-mono">B{bed.bed_no}</span>
                        {hasVariedRents && (
                          <span className="text-[10px] font-mono text-slate-600">₹{actualRent?.toLocaleString()}</span>
                        )}
                        {bed.guest_name ? (
                          <span className="text-[10px] font-semibold truncate max-w-full text-slate-900">{bed.guest_name.split(' ')[0]}</span>
                        ) : isBooked ? (
                          <span className="text-[10px] text-blue-600 font-semibold">Booked</span>
                        ) : (
                          canAddGuest && <span className="text-[10px] text-blue-600 font-medium">+ Add</span>
                        )}
                      </div>
                    );

                    if (isVacant || !bed.guest_id) {
                      return <div key={bed.bed_no}>{tile}</div>;
                    }

                    return (
                      <Popover key={bed.bed_no} open={openBedKey === bedKey} onOpenChange={(o) => { if (!o) setOpenBedKey(null); }}>
                        <PopoverAnchor asChild>{tile}</PopoverAnchor>
                        <PopoverContent align="center" sideOffset={8} className="w-72 p-0 overflow-hidden rounded-xl border-slate-200 shadow-xl" data-testid={`bed-dues-popover-${room.id}-${bed.bed_no}`}>
                          <div className={`px-4 py-3 ${bed.status === 'occupied' ? 'bg-gradient-to-r from-emerald-500 to-teal-600' : bed.status === 'notice' ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-blue-500 to-indigo-600'}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-white truncate leading-tight">{bed.guest_name || 'Guest'}</p>
                                <p className="text-[11px] text-white/80 font-mono mt-0.5">{room.combined_number} · Bed {bed.bed_no}</p>
                              </div>
                              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-white/20 text-white capitalize">{bed.status}</span>
                            </div>
                          </div>
                          <div className="px-4 py-1.5 bg-slate-50 border-b flex items-center justify-between">
                            <span className="text-[11px] text-slate-500">Due Date</span>
                            <span className="text-xs font-semibold text-slate-700">{bedDuesLoading ? '…' : (bedDues?.due_label || '—')}</span>
                          </div>
                          <div className="p-4">
                            {bedDuesLoading ? (
                              <div className="flex items-center justify-center py-5 text-slate-400"><Loader2 className="h-4 w-4 animate-spin mr-2" />Loading dues…</div>
                            ) : bedDues ? (
                              <>
                                <div className="space-y-2">
                                  {[['Rent', bedDues.rent_due], ['Electricity', bedDues.electricity_due], ['Penalty', bedDues.penalty_due]].map(([label, val]) => (
                                    <div key={label} className="flex items-center justify-between text-xs">
                                      <span className="text-slate-500">{label} Due</span>
                                      <span className={`font-mono font-medium ${(val || 0) > 0 ? 'text-slate-900' : 'text-slate-300'}`}>₹{(val || 0).toLocaleString()}</span>
                                    </div>
                                  ))}
                                  <div className="pt-2 mt-2 border-t border-slate-100 space-y-2">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-slate-500">Deposit Paid</span>
                                      <span className="font-mono font-medium text-emerald-600">₹{(bedDues.deposit_paid || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-slate-500">Advance Paid</span>
                                      <span className="font-mono font-medium text-emerald-600">₹{(bedDues.advance_paid || 0).toLocaleString()}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className={`mt-3 rounded-lg px-3 py-2 flex items-center justify-between ${bedDues.balance_type === 'Receivable' ? 'bg-rose-50' : bedDues.balance_type === 'Payable' ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Net Balance{bedDues.balance_type !== 'Settled' ? ` (${bedDues.balance_type})` : ''}</span>
                                  <span className={`font-mono font-bold text-base ${bedDues.balance_type === 'Receivable' ? 'text-rose-600' : bedDues.balance_type === 'Payable' ? 'text-emerald-600' : 'text-slate-500'}`}>
                                    ₹{Math.abs(bedDues.net_balance || 0).toLocaleString()}
                                  </span>
                                </div>
                                {bedDues.balance_type === 'Settled' && (
                                  <p className="mt-1.5 text-[11px] text-center font-medium text-slate-400">No outstanding dues</p>
                                )}
                              </>
                            ) : (
                              <p className="text-xs text-slate-400 text-center py-4">Could not load dues</p>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                  <span>Deposit: ₹{room.deposit_per_bed?.toLocaleString()}</span>
                  {(() => {
                    const allRents = room.beds?.map(b => b.guest_rent || b.rent || room.rent_per_bed) || [];
                    return new Set(allRents).size > 1;
                  })() && (
                    <span className="text-amber-600 font-medium">Varied rents</span>
                  )}
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
